import { useCallback, useMemo, useRef, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import {
  confirmCoupon,
  shareCoupon,
  unshareCoupon,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  confirmDelivery,
  shareGiftDelivery,
  unshareGiftDelivery,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryForStore,
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { giftOrderForStoreDataAtomFamily } from '@shared/api/giftOrder/giftOrder.atom';
import { updateGiftOrderCourtesy } from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftOrderDeliveryMode,
  GiftOrderForStore,
  GiftOrderItem,
  GiftOrderItemType,
  GiftStoreRole,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useQueryClient } from 'react-query';

import { ShareChannel } from '../containers/GiftCouponShareModal/GiftCouponShareModal';
import { SHARE_GUIDE_DISMISSED_KEY } from '../utils/giftOrderCoupon.utils';

type PendingConfirmItem =
  | { type: 'coupon'; item: GiftCouponForStore }
  | { type: 'delivery'; item: GiftDeliveryForStore };

interface UseGiftOrderShareParams {
  orderId: string;
  order: GiftOrderForStore | null;
  role: GiftStoreRole;
  courtesyContent: string;
}

export function useGiftOrderShare({
  orderId,
  order,
  role,
  courtesyContent,
}: UseGiftOrderShareParams) {
  const setOrderData = useSetRecoilState(giftOrderForStoreDataAtomFamily(orderId));
  const queryClient = useQueryClient();

  const syncCourtesyIfNeeded = useCallback(async () => {
    if (!orderId || !courtesyContent || order?.courtesyContent === courtesyContent)
      return;
    try {
      const updated = await updateGiftOrderCourtesy(orderId, { courtesyContent }, role);
      setOrderData((prev) =>
        prev ? { ...prev, courtesyContent: updated.courtesyContent } : prev,
      );
    } catch (error) {
      console.error('답례글 자동 저장 실패:', error);
    }
  }, [orderId, courtesyContent, order?.courtesyContent, role, setOrderData]);

  // === 쿠폰 목록 (취소된 쿠폰 제외) ===
  const couponList = useMemo(() => {
    return ((order?.coupons ?? []) as GiftCouponForStore[]).filter(
      (c) => c.redeemStatus !== GiftCouponRedeemStatus.Canceled,
    );
  }, [order]);

  // === 배송형 상품 ===
  const deliveryItems = useMemo(() => {
    if (!order?.orderDetails) return [];
    return order.orderDetails.filter(
      (item) => item.type === GiftOrderItemType.Delivery,
    ) as GiftOrderItem<GiftItemForStore, Image>[];
  }, [order]);

  const deliveryMode = order?.deliveryMode;
  const isIndividualDelivery = deliveryMode === GiftOrderDeliveryMode.Individual;
  const isBulkDelivery = deliveryMode === GiftOrderDeliveryMode.Bulk;

  const deliveryList = useMemo(() => {
    return ((order?.deliveries ?? []) as GiftDeliveryForStore[]).filter(
      (d) => d.deliveryStatus !== GiftDeliveryStatus.Canceled,
    );
  }, [order]);

  // === 공유 가이드 모달 ===
  const [showShareGuide, setShowShareGuide] = useState(false);
  const pendingShareRef = useRef<{
    type: 'coupon' | 'delivery';
    item: GiftCouponForStore | GiftDeliveryForStore;
  } | null>(null);

  // === 쿠폰 공유 모달 ===
  const couponShareModalOpenId = `gift-coupon-share-${orderId}`;
  const setCouponShareModalOpen = useSetRecoilState(
    simpleOpenAtomFamily(couponShareModalOpenId),
  );
  const [currentShareCoupon, setCurrentShareCoupon] = useState<GiftCouponForStore | null>(
    null,
  );
  const [couponShareOpenCount, setCouponShareOpenCount] = useState(0);

  const currentCouponShareUrl = useMemo(() => {
    if (!currentShareCoupon) return '';
    return `${window.location.origin}/gift-store/coupon/${currentShareCoupon.code}`;
  }, [currentShareCoupon]);

  const handleOpenCouponShareModal = useCallback(
    (coupon: GiftCouponForStore) => {
      setCurrentShareCoupon(coupon);
      setCouponShareOpenCount((c) => c + 1);
      setCouponShareModalOpen(true);
    },
    [setCouponShareModalOpen],
  );

  // 공유 채널(문자/카카오/다른앱) 클릭 시 → share API 호출 + 돌아오면 확인 모달
  const handleCouponShareComplete = useCallback(
    async (channel: ShareChannel) => {
      if (!currentShareCoupon) return;

      try {
        await Promise.all([
          shareCoupon(currentShareCoupon._id, role),
          syncCourtesyIfNeeded(),
        ]);
        setOrderData((prev) => {
          if (!prev || !prev.coupons) return prev;
          return {
            ...prev,
            coupons: prev.coupons.map((c) =>
              c._id === currentShareCoupon._id
                ? { ...c, shareStatus: GiftCouponShareStatus.Shared }
                : c,
            ),
          };
        });
      } catch (error) {
        console.error('공유 상태 업데이트 실패:', error);
      }

      setLastShareChannel(channel);
      setPendingConfirmItem({ type: 'coupon', item: currentShareCoupon });
      setShowShareConfirm(true);
    },
    [currentShareCoupon, role, setOrderData, syncCourtesyIfNeeded],
  );

  const handleCouponShare = useCallback(
    (coupon: GiftCouponForStore) => {
      const dismissed = localStorage.getItem(SHARE_GUIDE_DISMISSED_KEY) === 'true';
      if (dismissed) {
        handleOpenCouponShareModal(coupon);
      } else {
        pendingShareRef.current = { type: 'coupon', item: coupon };
        setShowShareGuide(true);
      }
    },
    [handleOpenCouponShareModal],
  );

  // === 배송 공유 모달 (Individual) ===
  const deliveryShareModalOpenId = `gift-delivery-share-${orderId}`;
  const setDeliveryShareModalOpen = useSetRecoilState(
    simpleOpenAtomFamily(deliveryShareModalOpenId),
  );
  const [currentShareDelivery, setCurrentShareDelivery] =
    useState<GiftDeliveryForStore | null>(null);
  const [deliveryShareOpenCount, setDeliveryShareOpenCount] = useState(0);

  const currentDeliveryShareUrl = useMemo(() => {
    if (!currentShareDelivery) return '';
    return `${window.location.origin}/gift-store/delivery/${currentShareDelivery.code}`;
  }, [currentShareDelivery]);

  const handleOpenDeliveryShareModal = useCallback(
    (delivery: GiftDeliveryForStore) => {
      setCurrentShareDelivery(delivery);
      setDeliveryShareOpenCount((c) => c + 1);
      setDeliveryShareModalOpen(true);
    },
    [setDeliveryShareModalOpen],
  );

  // 공유 채널(문자/카카오/다른앱) 클릭 시 → share API 호출 + 돌아오면 확인 모달
  const handleDeliveryShareComplete = useCallback(
    async (channel: ShareChannel) => {
      if (!currentShareDelivery) return;

      try {
        await Promise.all([
          shareGiftDelivery(currentShareDelivery._id, role),
          syncCourtesyIfNeeded(),
        ]);
        setOrderData((prev) => {
          if (!prev || !prev.deliveries) return prev;
          return {
            ...prev,
            deliveries: prev.deliveries.map((d) =>
              d._id === currentShareDelivery._id
                ? { ...d, shareStatus: GiftDeliveryShareStatus.Shared }
                : d,
            ),
          };
        });
      } catch (error) {
        console.error('배송 공유 상태 업데이트 실패:', error);
      }

      setLastShareChannel(channel);
      setPendingConfirmItem({ type: 'delivery', item: currentShareDelivery });
      setShowShareConfirm(true);
    },
    [currentShareDelivery, role, setOrderData, syncCourtesyIfNeeded],
  );

  const handleDeliveryShare = useCallback(
    (delivery: GiftDeliveryForStore) => {
      const dismissed = localStorage.getItem(SHARE_GUIDE_DISMISSED_KEY) === 'true';
      if (dismissed) {
        handleOpenDeliveryShareModal(delivery);
      } else {
        pendingShareRef.current = { type: 'delivery', item: delivery };
        setShowShareGuide(true);
      }
    },
    [handleOpenDeliveryShareModal],
  );

  // === 전달 확인 모달 (공유 채널 클릭 후 표시) ===
  const [showShareConfirm, setShowShareConfirm] = useState(false);
  const [pendingConfirmItem, setPendingConfirmItem] = useState<PendingConfirmItem | null>(
    null,
  );
  const [isConfirmSaving, setIsConfirmSaving] = useState(false);
  const [lastShareChannel, setLastShareChannel] = useState<ShareChannel>('kakao');

  // 메모 남기기 모달
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [pendingMemoItem, setPendingMemoItem] = useState<PendingConfirmItem | null>(null);
  const [isMemoSaving, setIsMemoSaving] = useState(false);

  const handleOpenMemoCoupon = useCallback((coupon: GiftCouponForStore) => {
    setPendingMemoItem({ type: 'coupon', item: coupon });
    setShowMemoModal(true);
  }, []);

  const handleOpenMemoDelivery = useCallback((delivery: GiftDeliveryForStore) => {
    setPendingMemoItem({ type: 'delivery', item: delivery });
    setShowMemoModal(true);
  }, []);

  const invalidateOrder = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey === 'string' &&
        query.queryKey.includes(`/gift-order/${orderId}`),
    });
  }, [queryClient, orderId]);

  // "네, 보냈어요" → confirm API 호출 (memo optional) + 데이터 새로고침
  const handleShareConfirm = useCallback(
    async (memo: string) => {
      if (!pendingConfirmItem) return;
      setIsConfirmSaving(true);

      try {
        const trimmedMemo = memo.trim() || undefined;

        if (pendingConfirmItem.type === 'coupon') {
          await confirmCoupon(pendingConfirmItem.item._id, role, trimmedMemo);
        } else {
          await confirmDelivery(pendingConfirmItem.item._id, role, trimmedMemo);
        }
        invalidateOrder();
      } catch (error) {
        console.error('전달 확인 실패:', error);
      } finally {
        setIsConfirmSaving(false);
        setShowShareConfirm(false);
        setPendingConfirmItem(null);
      }
    },
    [pendingConfirmItem, role, invalidateOrder],
  );

  const performUnshare = useCallback(async () => {
    if (!pendingConfirmItem) return null;
    setIsConfirmSaving(true);
    const item = pendingConfirmItem;
    try {
      if (item.type === 'coupon') {
        await unshareCoupon(item.item._id, role);
        setOrderData((prev) => {
          if (!prev || !prev.coupons) return prev;
          return {
            ...prev,
            coupons: prev.coupons.map((c) =>
              c._id === item.item._id
                ? { ...c, shareStatus: GiftCouponShareStatus.NotShared }
                : c,
            ),
          };
        });
      } else {
        await unshareGiftDelivery(item.item._id, role);
        setOrderData((prev) => {
          if (!prev || !prev.deliveries) return prev;
          return {
            ...prev,
            deliveries: prev.deliveries.map((d) =>
              d._id === item.item._id
                ? { ...d, shareStatus: GiftDeliveryShareStatus.NotShared }
                : d,
            ),
          };
        });
      }
    } catch (error) {
      console.error('공유 취소 실패:', error);
    } finally {
      setIsConfirmSaving(false);
      setShowShareConfirm(false);
      setPendingConfirmItem(null);
    }
    return item;
  }, [pendingConfirmItem, role, setOrderData]);

  // "다른 방법으로 보내기" → unshare + 해당 아이템 공유 모달 바로 다시 열기
  const handleShareConfirmDeny = useCallback(async () => {
    const item = await performUnshare();
    if (!item) return;
    if (item.type === 'coupon') {
      handleOpenCouponShareModal(item.item as GiftCouponForStore);
    } else {
      handleOpenDeliveryShareModal(item.item as GiftDeliveryForStore);
    }
  }, [performUnshare, handleOpenCouponShareModal, handleOpenDeliveryShareModal]);

  // "나중에 할게요" → unshare만 하고 모달 닫기
  const handleShareConfirmLater = useCallback(async () => {
    await performUnshare();
  }, [performUnshare]);

  const handleShareConfirmClose = useCallback(() => {
    setShowShareConfirm(false);
    setPendingConfirmItem(null);
  }, []);

  const handleMemoSave = useCallback(
    async (memo: string) => {
      if (!pendingMemoItem) return;
      const trimmed = memo.trim();
      if (!trimmed) return;
      setIsMemoSaving(true);
      try {
        if (pendingMemoItem.type === 'coupon') {
          await confirmCoupon(pendingMemoItem.item._id, role, trimmed);
        } else {
          await confirmDelivery(pendingMemoItem.item._id, role, trimmed);
        }
        invalidateOrder();
      } catch (error) {
        console.error('메모 저장 실패:', error);
      } finally {
        setIsMemoSaving(false);
        setShowMemoModal(false);
        setPendingMemoItem(null);
      }
    },
    [pendingMemoItem, role, invalidateOrder],
  );

  const handleMemoClose = useCallback(() => {
    setShowMemoModal(false);
    setPendingMemoItem(null);
  }, []);

  // === 하단 버튼 (쿠폰 + 배송 통합) ===
  const handleBottomButtonClick = useCallback(() => {
    const firstUnsharedCoupon = couponList.find(
      (c) => c.shareStatus === GiftCouponShareStatus.NotShared,
    );
    if (firstUnsharedCoupon) {
      handleCouponShare(firstUnsharedCoupon);
      return;
    }

    if (isIndividualDelivery) {
      const firstUnsharedDelivery = deliveryList.find(
        (d) => d.shareStatus === GiftDeliveryShareStatus.NotShared,
      );
      if (firstUnsharedDelivery) {
        handleDeliveryShare(firstUnsharedDelivery);
      }
    }
  }, [
    couponList,
    deliveryList,
    isIndividualDelivery,
    handleCouponShare,
    handleDeliveryShare,
  ]);

  const handleShareGuideConfirm = useCallback(() => {
    setShowShareGuide(false);
    if (pendingShareRef.current) {
      if (pendingShareRef.current.type === 'coupon') {
        handleOpenCouponShareModal(pendingShareRef.current.item as GiftCouponForStore);
      } else {
        handleOpenDeliveryShareModal(
          pendingShareRef.current.item as GiftDeliveryForStore,
        );
      }
      pendingShareRef.current = null;
    }
  }, [handleOpenCouponShareModal, handleOpenDeliveryShareModal]);

  // === 통계 (재발행 쿠폰 제외) ===
  const originalCouponList = couponList.filter((c) => !c.isAdditional);
  const totalCoupons = originalCouponList.length;
  const sharedCoupons = originalCouponList.filter(
    (c) => c.shareStatus !== GiftCouponShareStatus.NotShared,
  ).length;
  const unsharedCoupons = totalCoupons - sharedCoupons;

  const totalIndividualDeliveries = isIndividualDelivery ? deliveryList.length : 0;
  const sharedDeliveries = isIndividualDelivery
    ? deliveryList.filter((d) => d.shareStatus !== GiftDeliveryShareStatus.NotShared)
        .length
    : 0;
  const unsharedDeliveries = totalIndividualDeliveries - sharedDeliveries;

  const hasShareableItems = couponList.length > 0 || isIndividualDelivery;
  const totalUnshared = unsharedCoupons + unsharedDeliveries;

  return {
    couponList,
    deliveryItems,
    deliveryList,
    isIndividualDelivery,
    isBulkDelivery,

    couponShareModalOpenId,
    couponShareOpenCount,
    currentCouponShareUrl,
    handleCouponShare,
    handleCouponShareComplete,

    deliveryShareModalOpenId,
    deliveryShareOpenCount,
    currentDeliveryShareUrl,
    handleDeliveryShare,
    handleDeliveryShareComplete,

    showShareGuide,
    setShowShareGuide,
    handleBottomButtonClick,
    handleShareGuideConfirm,

    showShareConfirm,
    isConfirmSaving,
    lastShareChannel,
    handleShareConfirm,
    handleShareConfirmDeny,
    handleShareConfirmLater,
    handleShareConfirmClose,

    showMemoModal,
    isMemoSaving,
    handleOpenMemoCoupon,
    handleOpenMemoDelivery,
    handleMemoSave,
    handleMemoClose,

    totalCoupons,
    sharedCoupons,
    unsharedCoupons,
    totalIndividualDeliveries,
    sharedDeliveries,
    unsharedDeliveries,
    hasShareableItems,
    totalUnshared,
  };
}
